import { Link } from "react-router-dom";
import type { Customer } from "@/mocks/admin/customers";
import StatusBadge from "./StatusBadge";

const typeIcons: Record<string, { icon: string; label: string }> = {
  individual: { icon: "ri-user-line", label: "Individual" },
  business: { icon: "ri-briefcase-line", label: "Business" },
  "property-mgmt": { icon: "ri-building-line", label: "Property Mgmt" },
};

export default function CustomersTable({ customers }: { customers: Customer[] }) {
  if (customers.length === 0) {
    return (
      <div className="bg-white border border-ink-100 rounded-lg py-20 text-center">
        <div className="w-14 h-14 flex items-center justify-center rounded-full bg-cream-100 text-ink-400 mx-auto mb-3">
          <i className="ri-user-search-line text-2xl"></i>
        </div>
        <div className="font-display text-lg font-bold text-ink-900">No customers found</div>
        <div className="text-sm text-ink-500 mt-1">Try a different filter or search term.</div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-ink-100 rounded-lg overflow-hidden">
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-cream-50 border-b border-ink-100 text-left text-[11px] uppercase tracking-wider text-ink-500 font-semibold">
              <th className="py-3 px-4">Customer</th>
              <th className="py-3 px-3">Type</th>
              <th className="py-3 px-3">Status</th>
              <th className="py-3 px-3">Location</th>
              <th className="py-3 px-3 text-center">Moves</th>
              <th className="py-3 px-3 text-right">Lifetime Value</th>
              <th className="py-3 px-3">Last Activity</th>
              <th className="py-3 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {customers.map((c) => {
              const t = typeIcons[c.type];
              return (
                <tr key={c.id} className="hover:bg-cream-50 transition-colors group">
                  <td className="py-3 px-4">
                    <Link
                      to={`/customers/${c.id}`}
                      className="flex items-center gap-3 group-hover:text-brand-600"
                    >
                      <div
                        className={`w-9 h-9 rounded-full ${c.color} text-white text-xs font-bold flex items-center justify-center flex-shrink-0`}
                      >
                        {c.initials}
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-ink-900 group-hover:text-brand-600 truncate">
                          {c.name}
                        </div>
                        <div className="text-[11px] text-ink-500 truncate">{c.email}</div>
                      </div>
                    </Link>
                  </td>
                  <td className="py-3 px-3">
                    <span className="inline-flex items-center gap-1.5 text-xs text-ink-700">
                      <i className={`${t.icon} text-sm text-ink-400`}></i>
                      {t.label}
                    </span>
                  </td>
                  <td className="py-3 px-3">
                    <StatusBadge status={c.status} />
                  </td>
                  <td className="py-3 px-3 text-ink-700 whitespace-nowrap">
                    {c.city}, {c.state}
                  </td>
                  <td className="py-3 px-3 text-center">
                    <span className="inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded-md bg-cream-100 text-ink-900 text-xs font-bold tabular-nums">
                      {c.totalMoves}
                    </span>
                  </td>
                  <td className="py-3 px-3 text-right font-semibold text-ink-900 tabular-nums whitespace-nowrap">
                    {c.lifetimeValue > 0 ? `$${c.lifetimeValue.toLocaleString()}` : "—"}
                  </td>
                  <td className="py-3 px-3 text-xs text-ink-500 whitespace-nowrap">
                    {c.lastMoveRelative}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        title="Email"
                        className="w-8 h-8 flex items-center justify-center rounded-md text-ink-400 hover:text-ink-900 hover:bg-cream-100 cursor-pointer"
                      >
                        <i className="ri-mail-line"></i>
                      </button>
                      <button
                        title="Call"
                        className="w-8 h-8 flex items-center justify-center rounded-md text-ink-400 hover:text-ink-900 hover:bg-cream-100 cursor-pointer"
                      >
                        <i className="ri-phone-line"></i>
                      </button>
                      <Link
                        to={`/customers/${c.id}`}
                        title="View"
                        className="w-8 h-8 flex items-center justify-center rounded-md text-ink-400 hover:text-ink-900 hover:bg-cream-100 cursor-pointer"
                      >
                        <i className="ri-arrow-right-line"></i>
                      </Link>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}